import { TagIcon } from '@heroicons/react/outline';
import { OAuthError, patchData, postData } from '../backend';
import { SourceField, Workflow } from '../interfaces';
import { useEffect, useState } from 'react';
import { Popover } from '@headlessui/react';
import { useModal } from '../contexts';
import Modal from './Modal';
import { KeyedMutator } from "swr";
import { useTranslation } from 'react-i18next';
import ToggleButton from './ToggleButton';
import OAuthModalMicrosoft from './OAuthModalMicrosoft';
import useUser from '../hooks/useUser';

interface Props {
  workflow: Workflow;
  mutateWorkflow: KeyedMutator<Workflow>;
}

function WorkflowOutputOneDriveModal(props: Props) {
  const { user } = useUser();
  const { workflow, mutateWorkflow } = props;
  const { openModal, closeModal } = useModal();
  const { t } = useTranslation();


  const [isSaving, setIsSaving] = useState(false);

  const [microsoftOauthUrl, setMicrosoftOauthUrl] = useState<string>();
  const [onedriveScopeProvided, setOnedriveScopeProvided] = useState<boolean>();

  const [onedriveDisabled, setOnedriveDisabled] = useState<boolean>(workflow.onedriveDisabled);
  const [onedriveFolderPath, setOnedriveFolderPath] = useState<string>(workflow.onedriveFolderPath || "");

  function updateWorkflow() {
    const updates: Partial<Workflow> = { onedriveDisabled, onedriveFolderPath };

    setIsSaving(true);
    patchData(`/workflows/${workflow.id}/`, updates)
      .then(() => mutateWorkflow())
      .then(() => closeModal())
      .catch(console.error)
      .finally(() => setIsSaving(false));
  }

  function toggleOnedriveDisabled(value: boolean) {
    if (microsoftOauthUrl)
      return openModal(<OAuthModalMicrosoft oauthUrl={microsoftOauthUrl} onClose={() => openModal(<WorkflowOutputOneDriveModal {...props} />)} />);

    setOnedriveDisabled(!value);
  }

  useEffect(() => {
    if (onedriveScopeProvided !== undefined)
      return;

    postData("/microsoft/access-token/?scopes=Files.ReadWrite.All")
      .then(() => {
        setMicrosoftOauthUrl(undefined);

        setOnedriveScopeProvided(true);
        setOnedriveDisabled(workflow.onedriveDisabled);
      })
      .catch(error => {
        setOnedriveDisabled(true);
        setOnedriveScopeProvided(false);

        if (error instanceof OAuthError)
          setMicrosoftOauthUrl(error.oauthUrl);
        else
          console.error(error);
      });

  }, [onedriveScopeProvided, workflow.onedriveDisabled]);

  return (
    <Modal
      title={t("Output Folder")}
      secondaryButtonProps={{
        text: t("Cancel"),
        onClick: closeModal,
        className: `${workflow.outputFolder ? '' : 'hidden'}`
      }}
      primaryButtonProps={{
        text: t("Save"),
        onClick: updateWorkflow,
        disabled: isSaving,
        className: `${workflow.outputFolder ? '' : 'hidden'}`
      }}
    >
      <div className="text-gray-600 font-gilroy font-semibold flex items-center justify-between">
        {t("Save to Microsoft OneDrive")}
        {(!user?.useServiceOauth || onedriveDisabled) &&
          <ToggleButton value={!onedriveDisabled} onChange={toggleOnedriveDisabled} disabled={onedriveScopeProvided === undefined} />
        }
      </div>
      <div className='text-sm font-semibold text-gray-500 -mt-2'>
        {t("All files that are generated with a Microsoft output type will be automatically saved to your OneDrive. You can specify which folder these files are saved to using template tags or direct names.")}
      </div>
      <div className='relative flex items-center w-full'>
        <div className='border-2 flex items-center roudned w-full text-sm font-semibold font-gilroy rounded'>
          <div className='flex-shrink-0 bg-gray-200 p-2'>
            OneDrive
          </div>
          <div className='mx-1'>
            /
          </div>
          <input type="text" className='w-full h-full outline-none pr-2' placeholder='{{Template Tag}} / Folder Name' value={onedriveFolderPath} onChange={e => setOnedriveFolderPath(e.currentTarget.value)} disabled={onedriveDisabled} />
          {!onedriveDisabled &&
            <Popover className="relative right-1 top-px">
              {({ open }) => (
                <>
                  <Popover.Button className={`relative border rounded-md py-1 px-1 ml-2  bg-white ${open ? "border-blue" : "border-blue"}`}>
                    <TagIcon className={`w-4 h-4 cursor-pointer ${open ? "text-blue" : "text-blue"}`} />
                  </Popover.Button>
                  <Popover.Panel className="absolute mt-1 right-0 z-40 flex flex-col max-h-52 w-40 bg-white border rounded shadow overflow-y-auto">
                    {workflow.source?.sourceFields.map((sourceField: SourceField) => (
                      <Popover.Button key={sourceField.id} className="text-left text-sm border-b border-gray-400 last:border-none px-2 py-1 hover:bg-blue hover:bg-opacity-10 cursor-pointer" onClick={() => setOnedriveFolderPath(s => s + "{{" + sourceField.fieldName + "}}")}>
                        {sourceField.fieldName}
                      </Popover.Button>
                    ))}
                  </Popover.Panel>
                </>
              )}
            </Popover>
          }
        </div>
      </div>
    </Modal>
  );
}

export default WorkflowOutputOneDriveModal;
